import { ref, child, get, push, set, update, remove, onValue, query, orderByChild, equalTo } from 'firebase/database';
import { database } from './firebase';

const coursesRef = () => ref(database, 'courses');
const examsRef = (courseId) => ref(database, `exams/${courseId}`);
const attemptsRef = () => ref(database, 'examAttempts');
const environmentsRef = () => ref(database, 'environments');

const snapshotToArray = (snapshot) => {
  if (!snapshot.exists()) return [];
  return Object.entries(snapshot.val()).map(([id, data]) => ({ id, ...data }));
};

// Courses MANAGER
export const createCourse = async (name, id) => {
  const courseRef = id ? child(coursesRef(), id) : push(coursesRef());
  const courseId = id || courseRef.key;

  await set(courseRef, { id: courseId, name });
  return { id: courseId, name };
};

export const getCourses = async () => {
  const snapshot = await get(coursesRef());
  return snapshotToArray(snapshot);
};

export const subscribeToCourses = (callback) =>
  onValue(coursesRef(), (snapshot) => {
    callback(snapshotToArray(snapshot));
  });

export const deleteCourse = (id) => remove(child(coursesRef(), id));

// EXAM MANAGER
export const createExam = async (courseId, exam) => {
  const { id, name, questions = {} } = exam;
  const examRef = id ? child(examsRef(courseId), id) : push(examsRef(courseId));
  const examId = id || examRef.key;

  await set(examRef, { id: examId, name, questions });
  return { id: examId, name, questions };
};

export const updateExam = (courseId, examId, data) =>
  update(child(examsRef(courseId), examId), data);

export const deleteExam = (courseId, examId) =>
  remove(child(examsRef(courseId), examId));

export const getExamsByCourse = async (courseId) => {
  const snapshot = await get(examsRef(courseId));
  return snapshotToArray(snapshot);
};

export const getExam = async (examId, courseId) => {
  const snapshot = await get(child(examsRef(courseId), examId));
  return snapshot.exists() ? { id: examId, ...snapshot.val() } : null;
};

export const subscribeToExams = (courseId, callback) =>
  onValue(examsRef(courseId), (snapshot) => {
    callback(snapshotToArray(snapshot));
  });


// ATTEMPT MANAGER
export const createExamAttempt = async ({userId, examId, courseId, environment, startedAt = Date.now() }) => {
  const snapshot = await get(attemptsRef());
  const attempts = snapshotToArray(snapshot).filter(
    (attempt) =>
      attempt.userId === userId &&
      attempt.examId === examId &&
      attempt.courseId === courseId &&
      attempt.environment === environment,
  );

  const latestAttempt = attempts.sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0)).at(0);
  if (latestAttempt && !latestAttempt.finishedAt) {
    return latestAttempt;
  }

  const attemptRef = push(attemptsRef());
  const attempt = {id: attemptRef.key, userId, examId, courseId, environment, startedAt };

  await set(attemptRef, attempt);
  return attempt;
};

export const finishExamAttempt = (attemptId, finishedAt = Date.now()) =>
  update(child(attemptsRef(), attemptId), { finishedAt });

export const getExamAttemptsByUser = async (userId, environment) => {
  const snapshot = await get(attemptsRef());
  return snapshotToArray(snapshot).filter(
    (attempt) => attempt.userId === userId && attempt.environment === environment,
  );
};

export const getExamAttemptsByExam = async (examId, environment) => {
  const snapshot = await get(attemptsRef());
  return snapshotToArray(snapshot).filter(
    (attempt) => attempt.examId === examId && attempt.environment === environment,
  );
};

export const getExamAttemptsByExamByUser = async (examId, userId, environment) => {
  const attemptsQuery = query(attemptsRef(), orderByChild('userId'), equalTo(userId));
  const snapshot = await get(attemptsQuery);
  if (!snapshot.exists()) return [];

  return snapshotToArray(snapshot).filter((attempt) => attempt.examId === examId  && attempt.environment === environment);
};


// ENVIRONMENT MANAGER
export const createAvailablesEnv = async (name, available) => {
  const envRef = push(environmentsRef());
  await set(envRef, { name, available });
  return { name, available };
};

export const getAvailablesEnvironments = async () => {
  const snapshot = await get(environmentsRef());
  return snapshotToArray(snapshot).filter((environment) => environment.available === true);
};

export const itsAvailablesEnvironment = async (environmentName) => {
  if (!environmentName) {
    return false;
  }

  const snapshot = await get(environmentsRef());
  if (!snapshot.exists()) {
    return false;
  }

  const environments = Object.values(snapshot.val());
  const found = environments.find((env) => env.name === environmentName);
  return Boolean(found?.available);
};